import { useEffect, useMemo, useState } from "react";
import SearchInput from "./shared/SearchInput";
import { cssUrl } from "../utils/cssVersion";

// SearchInput uses the fr-* classes from this stylesheet — SubcontractorComplete
// doesn't load it, so the modal loads it while mounted (same as ComplianceExpiryModal).
const filterTemplateStyles = cssUrl("filterTemplate.css");

/* ============================================================================
 * SubcontractorModal
 * ----------------------------------------------------------------------------
 * Opened from the "Subcontractor Final Onboarding" icon in SubcontractorComplete.
 * Shows 4 buttons driven by the Airtable Subbie_Count formula:
 *
 *   3 → Subbie Ready         Working, and Subbie / Active Clients / Position all blank
 *   2 → Subbie In Progress   Not Working, and all three blank
 *   1 → Needs Amendment      one or more of the three is blank
 *   0 → Complete Data        all three filled (blank formula result also lands here)
 *
 * Clicking a button drops a table (Headshot, Cleaner, Email, Mobile, ABN Number);
 * clicking a row hands the record back via onSelect.
 *
 * Props:
 *   records  – array from fetchFinalSubbies (SubcontractorComplete state)
 *   onSelect – (record) => void
 *   onClose  – () => void
 * ==========================================================================*/

export const STATUS_BUCKETS = [
  {
    key: "ready",
    label: "Subbie Ready",
    count: 3,
    color: "#4ade80",
    hint: "Working — Subbie, Active Clients and Position not yet set",
  },
  {
    key: "progress",
    label: "Subbie In Progress",
    count: 2,
    color: "#22d3ee",
    hint: "Not Working — Subbie, Active Clients and Position not yet set",
    selectable: false, // in progress: other factors must play out before it reaches Subbie Ready
  },
  {
    key: "amend",
    label: "Needs Amendment",
    count: 1,
    color: "#f87171",
    hint: "One or more of Subbie, Active Clients or Position is missing",
  },
  {
    key: "complete",
    label: "Complete Data",
    count: 0,
    color: "#a78bfa",
    hint: "Subbie, Active Clients and Position are all filled",
  },
];

// Map a record's Subbie_Count to a bucket key. Anything unexpected (blank,
// non-numeric) is treated as 0 = Complete, matching the Airtable formula.
export const onboardingBucket = (record) => {
  const n = Number(record?.subbieCount ?? 0);
  return STATUS_BUCKETS.find((b) => b.count === n) ?? STATUS_BUCKETS[3];
};

/* ---------- styles (mirrors ComplianceExpiryModal) ---------- */
const S = {
  backdrop: {
    position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(2,6,23,0.7)", zIndex: 1100,
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "40px 16px", overflowY: "auto",
  },
  modal: {
    position: "relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width: "100%", maxWidth: 920,
    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    border: "1px solid rgba(34,211,238,0.25)", borderRadius: 16,
    padding: "18px 18px 14px",
  },
  panelAccent: {
    position: "absolute", top: 0, left: 0, right: 0, height: 3,
    background: "linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)",
  },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 },
  title: { margin: 0, fontSize: "1rem", fontWeight: 700, color: "#f1f5f9" },
  closeX: {
    background: "none", border: "none", color: "rgba(255,255,255,0.6)",
    fontSize: "1.4rem", lineHeight: 1, cursor: "pointer",
  },
  eyebrow: {
    fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase",
    color: "#22d3ee", fontWeight: 700,
  },
  buttonsGrid: {
    display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10, marginBottom: 8,
  },
  countBtn: (active, color) => ({
    borderRadius: 12, padding: "12px 10px", cursor: "pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border: `1px solid ${active ? color : "rgba(34,211,238,0.25)"}`,
    display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
    transition: "background .12s ease, border-color .12s ease",
  }),
  countLabel: {
    fontSize: "0.68rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase",
    letterSpacing: "0.05em", fontWeight: 700, textAlign: "center", lineHeight: 1.3,
  },
  countValue: (color) => ({ fontSize: "1.7rem", fontWeight: 800, lineHeight: 1, color }),
  card: {
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "12px", marginTop: 14,
  },
  colHead: {
    fontSize: "0.62rem", letterSpacing: "0.05em", textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rowName: {
    fontSize: "0.85rem", fontWeight: 700, color: "#22d3ee",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  cell: {
    fontSize: "0.84rem", color: "#e2e8f0",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  headshot: {
    width: 36, height: 36, borderRadius: "50%", objectFit: "cover", display: "block",
    border: "1px solid rgba(34,211,238,0.3)",
  },
  headshotEmpty: {
    width: 36, height: 36, borderRadius: "50%",
    border: "1px dashed rgba(34,211,238,0.3)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "0.7rem", fontWeight: 700, color: "rgba(255,255,255,0.4)",
  },
  empty: {
    padding: "24px", textAlign: "center", color: "rgba(255,255,255,0.5)",
    fontStyle: "italic", fontSize: "0.85rem",
  },
  hint: { marginTop: 10, fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", textAlign: "center" },
  closeBtn: {
    width: "100%", padding: "11px", borderRadius: 10, border: "none",
    background: "rgba(255,255,255,0.06)", color: "#e2e8f0", fontWeight: 600,
    cursor: "pointer", marginTop: 12,
  },
};

const TABLE_COLS = "56px 1.4fr 1.8fr 1.1fr 1.1fr";

const initials = (name) =>
  (name ?? "")
    .toString()
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("") || "—";

/* ---------- component ---------- */
const SubcontractorModal = ({ records, onSelect, onClose }) => {
  const [selectedKey, setSelectedKey] = useState(null);
  const [nameQuery, setNameQuery] = useState("");

  /* Load filterTemplate.css while the modal is open (fr-search-*) */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = filterTemplateStyles;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  /* Bucket every record once per open, sorted by name */
  const buckets = useMemo(() => {
    const out = {};
    STATUS_BUCKETS.forEach((b) => (out[b.key] = []));
    (records || []).forEach((r) => out[onboardingBucket(r).key].push(r));
    const byName = (a, b) => (a.name ?? "").localeCompare(b.name ?? "");
    Object.values(out).forEach((list) => list.sort(byName));
    return out;
  }, [records]);

  const activeBucket = selectedKey
    ? STATUS_BUCKETS.find((b) => b.key === selectedKey)
    : null;

  const bucketRows = activeBucket ? buckets[activeBucket.key] : [];
  const selectable = activeBucket ? activeBucket.selectable !== false : false;

  // Name search only narrows the table — button totals stay the same
  const activeRows = useMemo(() => {
    const q = nameQuery.toLowerCase().replace(/\s+/g, "");
    if (!q) return bucketRows;
    return bucketRows.filter((r) =>
      (r.name ?? "").toLowerCase().replace(/\s+/g, "").includes(q)
    );
  }, [bucketRows, nameQuery]);

  const toggle = (key) => setSelectedKey((prev) => (prev === key ? null : key));

  return (
    <div style={S.backdrop} onClick={onClose}>
      <style>{`
        .sc-row { transition: background .12s ease; }
        .sc-row:hover { background: rgba(34,211,238,0.07); }
      `}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />

        <div style={S.head}>
          <h4 style={S.title}>Subcontractor Final Onboarding</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* Name search — narrows the table rows below */}
        <div style={{ margin: "0 0 14px" }}>
          <SearchInput
            value={nameQuery}
            onChange={setNameQuery}
            placeholder="Search name in results…"
          />
        </div>

        <div style={{ ...S.eyebrow, margin: "0 2px 8px" }}>Onboarding status</div>
        <div style={S.buttonsGrid}>
          {STATUS_BUCKETS.map((b) => (
            <button
              key={b.key}
              type="button"
              onClick={() => toggle(b.key)}
              style={S.countBtn(selectedKey === b.key, b.color)}
              title={b.hint}
            >
              <span style={S.countLabel}>{b.label}</span>
              <span style={S.countValue(b.color)}>{buckets[b.key].length}</span>
            </button>
          ))}
        </div>

        {/* Filtered table */}
        {activeBucket && (
          <div style={S.card}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <span style={{ ...S.eyebrow, color: activeBucket.color }}>
                {activeBucket.label}
              </span>
              <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)" }}>
                {nameQuery
                  ? `${activeRows.length} of ${bucketRows.length}`
                  : activeRows.length}{" "}
                record{activeRows.length === 1 ? "" : "s"}
              </span>
            </div>

            {activeRows.length === 0 ? (
              <div style={S.empty}>
                {nameQuery
                  ? `No names matching “${nameQuery}” in this list.`
                  : "No records in this list."}
              </div>
            ) : (
              <div style={{ maxHeight: 340, overflowY: "auto", overflowX: "auto" }}>
                <div style={{ minWidth: 640 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: TABLE_COLS,
                      alignItems: "end",
                      padding: "8px 8px 10px",
                      borderBottom: "1px solid rgba(255,255,255,0.12)",
                      columnGap: 8,
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      background: "#1b2537",
                    }}
                  >
                    <span style={S.colHead}>Headshot</span>
                    <span style={S.colHead}>Cleaner</span>
                    <span style={S.colHead}>Email</span>
                    <span style={S.colHead}>Mobile</span>
                    <span style={S.colHead}>ABN Number</span>
                  </div>

                  {activeRows.map((r, i) => (
                    <div
                      key={r.recordId || i}
                      className={selectable ? "sc-row" : "sc-row-static"}
                      onClick={selectable ? () => onSelect(r) : undefined}
                      style={{
                        display: "grid",
                        gridTemplateColumns: TABLE_COLS,
                        alignItems: "center",
                        padding: "8px",
                        cursor: selectable ? "pointer" : "default",
                        opacity: selectable ? 1 : 0.7,
                        borderRadius: 8,
                        borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
                        columnGap: 8,
                      }}
                      title={
                        selectable
                          ? `Open ${r.name || "record"} in Subcontractor Complete`
                          : "In progress — opens once it reaches Subbie Ready"
                      }
                    >
                      <span>
                        {r.photo ? (
                          <img src={r.photo} alt={r.name || "Headshot"} style={S.headshot} />
                        ) : (
                          <span style={S.headshotEmpty}>{initials(r.name)}</span>
                        )}
                      </span>
                      <span style={S.rowName}>{r.name || "—"}</span>
                      <span style={S.cell} title={r.email || ""}>{r.email || "—"}</span>
                      <span style={S.cell}>{r.mobile || "—"}</span>
                      <span style={S.cell}>{r.abnNumber || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={S.hint}>
              {selectable
                ? "Click a record to open it for editing."
                : "These records are still in progress and can't be opened until they reach Subbie Ready."}
            </div>
          </div>
        )}

        <button type="button" style={S.closeBtn} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

export default SubcontractorModal;